import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import GroundVehicleMotionModel from './GroundVehicleMotionModel.js';
import HitVolume from '../weapons/HitVolume.js';
import { DEG2RAD, rand, srand } from '../utils.js';

const _emitterWorld = new THREE.Vector3();
const _turretWorld  = new THREE.Vector3();
const _radarWorld   = new THREE.Vector3();
const _launchQuat   = new THREE.Quaternion();
const _launchDir    = new THREE.Vector3();
const _negZ         = new THREE.Vector3(0, 0, -1);
const TWO_PI = Math.PI * 2;

// Damage-smoke cadence — same throttled, random-interval emitter as the other
// ground vehicles so a burning wreck doesn't flood the particle pool.
const SMOKE_INTERVAL_MIN = 0.2;
const SMOKE_INTERVAL_MAX = 0.4;

/**
 * 2K22 Tunguska — a stationary, radar-equipped anti-aircraft missile vehicle.
 *
 * Shares the ground-vehicle skeleton (model clone + GroundVehicleMotionModel
 * terrain conformance + destruction-keeps-the-wreck) with EnemyGroundVehicle,
 * but differs in three ways:
 *
 *   1. Always stationary — the body never drives or steers (no AI driver, no
 *      formation). It still samples four contact points so it conforms to the
 *      slope it spawns on.
 *   2. Turret + radar — the `turret` mesh slews to face the player; its child
 *      `radar` mesh spins continuously (~1 rev/s) like a surveillance antenna.
 *   3. Armed — carries a magazine of generic enemy missiles, launched at the
 *      A-10 from the turret origin on a fixed interval whenever the player is
 *      inside engagement range. Radar-equipped, so it trips the player's RWR.
 */
export default class EnemyTunguska {
  constructor({
    model, params, position, heading, scene,
    particleSystem, explosionSystem = null, audioManager = null, missileSystem = null,
    initialHealth = 100,
  }) {
    this.params = params;
    this.particleSystem = particleSystem;
    this.explosionSystem = explosionSystem;
    this.audioManager = audioManager;
    this.missileSystem = missileSystem;
    this.health = initialHealth;
    this.dead = false;          // never set — the wreck stays in the scene
    this.destroyed = false;     // true once health hits 0
    this._terrainCollider = null; // cached from update() for the death dust burst
    this.paused = true;         // dormant until EnemyManager clears the takeoff grace

    // Audio tag — the launcher never moves, so it feeds the shared *idle* loop
    // (AUDIO.enemyLoops.tunguska) rather than the driving one.
    this.audioLoop = 'tunguska';

    // RWR tagging — the Tunguska carries acquisition radar and SAMs, so it
    // both sweeps the receiver and raises the steady tracking tone.
    this.radarEquipped = true;

    // Live radar-contact state, recomputed each frame from range + elevation
    // cone + terrain line-of-sight. The player's RWR reads this flag (instead of
    // a plain max-range sphere) and the launcher won't fire without it, so
    // flying low/far ("under the radar") or masking behind a ridge breaks lock.
    this.radarContact = false;

    // AIM-9 is air-to-air — its IR seeker won't acquire a cool ground target.
    this.isGroundTarget = true;

    // Missile magazine — pure bookkeeping; rounds spawn from the missile system
    // at fire time. Cooldown only counts down while the player is in range.
    this.missilesRemaining = params.missileMagazine;
    this._fireCooldown = rand(params.firstFireDelay[0], params.firstFireDelay[1]);

    this.modelRoot = cloneSkeleton(model);
    this.modelRoot.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    this.group = new THREE.Group();
    this.group.add(this.modelRoot);
    scene.add(this.group);

    // Stationary motion model: desiredSpeed pinned to 0 and desiredHeading held
    // at the spawn heading so the body conforms to terrain without ever moving
    // or yawing (maxSpeed/maxYawRate are also 0 in config as a belt-and-braces).
    this.flight = new GroundVehicleMotionModel(params);
    this.flight.position.copy(position);
    this.flight.heading = heading;
    this.flight.desiredHeading = heading;
    this.flight.desiredSpeed = 0;

    this._smokeTimer = rand(SMOKE_INTERVAL_MIN, SMOKE_INTERVAL_MAX);

    // Turret + radar meshes. The turret slews about its local Y to track the
    // player; the radar spins about its own axis. Capture the turret's rest
    // yaw so the aiming delta composes onto the authored pose instead of
    // clobbering it (the GLB bakes a +90° Y rotation into the turret node).
    this.turretNode = this.modelRoot.getObjectByName(params.turretMesh);
    if (!this.turretNode) {
      console.warn(`EnemyTunguska: turret mesh "${params.turretMesh}" not found`);
    }
    this._turretRestYaw = this.turretNode ? this.turretNode.rotation.y : 0;
    this._turretDelta = 0;   // current yaw offset from rest (rad)

    this.radarNode = this.modelRoot.getObjectByName(params.radarMesh);
    if (!this.radarNode) {
      console.warn(`EnemyTunguska: radar mesh "${params.radarMesh}" not found`);
    }
    this._radarSpin = 0;

    // Collision volume — same 'Hitbox' convention as the other ground vehicles.
    const hitboxNode = this.modelRoot.getObjectByName('Hitbox');
    if (hitboxNode) {
      if (hitboxNode.isMesh) hitboxNode.visible = false;
      this.hitVolume = new HitVolume(hitboxNode);
    } else {
      this.hitVolume = null;
      console.warn('EnemyTunguska: "Hitbox" node not found in model');
    }

    // Damage smoke emitter anchor on the model.
    this.emitterNode = this.modelRoot.getObjectByName('Emitter');
    if (!this.emitterNode) {
      console.warn('EnemyTunguska: "Emitter" node not found in model');
    }
  }

  /** RWR: raise the tracking tone only while there are still rounds on the rail. */
  get missileEquipped() {
    return this.missilesRemaining > 0 && this.missileSystem != null;
  }

  update(dt, terrainCollider, player = null) {
    // Held for the death explosion's dust scatter, which needs ground heights
    // out to a radius and is raised from onProjectileHit rather than update.
    this._terrainCollider = terrainCollider;

    // Conform to terrain — the model is stationary, but slope pitch/roll/Y are
    // still resolved each frame in case the tile streamed in after spawn.
    this.flight.update(dt, terrainCollider);
    this.group.position.copy(this.flight.position);
    this.group.quaternion.copy(this.flight.quaternion);

    if (this.hitVolume) this.hitVolume.update();

    // Dormant during the takeoff grace: sit conformed to terrain but don't
    // track, spin the radar, or fire, and drop off the player's RWR.
    if (this.paused) {
      this.radarContact = false;
      this._updateSmokeTrail(dt);
      return;
    }

    // A live vehicle tracks + engages the player; a wreck freezes its turret
    // and radar in place and drops off the player's RWR.
    if (!this.destroyed) {
      this._updateRadarContact(player, terrainCollider);
      if (this._fireCooldown > 0) this._fireCooldown -= dt;
      this._aimTurret(dt, player);
      this._spinRadar(dt);
      this._updateFire(dt, player);
    } else {
      this.radarContact = false;
    }

    this._updateSmokeTrail(dt);
  }

  // ── Radar detection ──────────────────────────────────────────────────────

  // Decide whether the radar currently sees the player. Three gates, cheapest
  // first so the expensive terrain march only runs for a plausible contact:
  //   1. Range   — within radarRange of the antenna.
  //   2. Cone    — the player must sit above radarMinElevationDeg measured from
  //                the antenna. Below that line they're lost in ground clutter,
  //                i.e. "flying under the radar" (low + far is the way through).
  //   3. Masking — a coarse line-of-sight ray-march; terrain in the way blocks
  //                the lock even when range + elevation would otherwise allow it.
  _updateRadarContact(player, terrainCollider) {
    this.radarContact = false;
    if (!player) return;

    // Antenna world position (fall back to the hull origin if the mesh is absent).
    if (this.radarNode) this.radarNode.getWorldPosition(_radarWorld);
    else                _radarWorld.copy(this.flight.position);

    const dx = player.position.x - _radarWorld.x;
    const dy = player.position.y - _radarWorld.y;
    const dz = player.position.z - _radarWorld.z;

    // 1) Range gate.
    const horiz = Math.hypot(dx, dz);
    const dist  = Math.hypot(horiz, dy);
    if (dist > this.params.radarRange) return;

    // 2) Elevation cone — atan2(up, ground-range) must clear the minimum angle.
    const elevation = Math.atan2(dy, horiz);
    if (elevation < this.params.radarMinElevationDeg * DEG2RAD) return;

    // 3) Terrain masking (skipped if the collider isn't available yet).
    if (terrainCollider && !terrainCollider.hasLineOfSight(
      _radarWorld.x, _radarWorld.y, _radarWorld.z,
      player.position.x, player.position.y, player.position.z,
      this.params.radarRayStep,
    )) return;

    this.radarContact = true;
  }

  // ── Turret + radar ─────────────────────────────────────────────────────────

  // Slew the turret about its local Y so its forward (-Z in the body frame)
  // points at the player. The aim is computed as a world bearing, converted to
  // a delta off the body heading, then chased under a traverse-rate cap so the
  // turret swings around at a believable speed rather than snapping.
  _aimTurret(dt, player) {
    if (!this.turretNode || !player) return;

    this.turretNode.getWorldPosition(_turretWorld);
    const dx = player.position.x - _turretWorld.x;
    const dz = player.position.z - _turretWorld.z;
    // Sim yaw convention: forward = (-sin h, -cos h), so bearing = atan2(-dx,-dz).
    const bearing = Math.atan2(-dx, -dz);

    // Desired turret offset relative to the (possibly slope-tilted) hull.
    let targetDelta = bearing - this.flight.heading;
    while (targetDelta >  Math.PI) targetDelta -= TWO_PI;
    while (targetDelta < -Math.PI) targetDelta += TWO_PI;

    // Shortest-path slew toward the target offset, capped by the traverse rate.
    let err = targetDelta - this._turretDelta;
    while (err >  Math.PI) err -= TWO_PI;
    while (err < -Math.PI) err += TWO_PI;
    const maxStep = this.params.turretSlewRate * dt;
    this._turretDelta += Math.max(-maxStep, Math.min(maxStep, err));

    this.turretNode.rotation.y = this._turretRestYaw + this._turretDelta;
  }

  // Continuous antenna sweep — accumulate about the configured local axis and
  // wrap to keep the float bounded.
  _spinRadar(dt) {
    if (!this.radarNode) return;
    this._radarSpin = (this._radarSpin + this.params.radarSpinRate * dt) % TWO_PI;
    this.radarNode.rotation[this.params.radarSpinAxis] = this._radarSpin;
  }

  // ── Missile firing ─────────────────────────────────────────────────────────

  _updateFire(dt, player) {
    if (!player || !this.missileSystem) return;
    if (this.missilesRemaining <= 0 || this._fireCooldown > 0) return;
    // No radar lock (under the cone or masked by terrain) → hold fire.
    if (!this.radarContact) return;

    const dx = player.position.x - this.flight.position.x;
    const dy = player.position.y - this.flight.position.y;
    const dz = player.position.z - this.flight.position.z;
    const range2 = dx * dx + dy * dy + dz * dz;
    const r = this.params.fireRange;
    if (range2 > r * r) return;   // hold fire until the player closes inside range

    this._fire(player);
    this._fireCooldown = this.params.fireInterval
      + rand(-this.params.fireIntervalRandom, this.params.fireIntervalRandom);
  }

  // Launch one missile from the turret origin. It fires upward at a fixed
  // elevation, pointed in azimuth toward the player; PN guidance then pulls it
  // over onto the target. A lofted launch reads as a SAM and avoids skimming
  // the horizon (or worse, the ground) when the A-10 is low or level with us.
  _fire(player) {
    if (this.turretNode) this.turretNode.getWorldPosition(_turretWorld);
    else                 _turretWorld.copy(this.flight.position);

    // Azimuth toward the player (XZ), tilted up by launchElevationDeg.
    const dx = player.position.x - _turretWorld.x;
    const dz = player.position.z - _turretWorld.z;
    const horiz = Math.hypot(dx, dz) || 1;
    const el = this.params.launchElevationDeg * DEG2RAD;
    const cosEl = Math.cos(el);
    _launchDir.set((dx / horiz) * cosEl, Math.sin(el), (dz / horiz) * cosEl);

    // Orient the missile so its -Z forward points along the launch direction.
    _launchQuat.setFromUnitVectors(_negZ, _launchDir);

    const ok = this.missileSystem.launch(_turretWorld, _launchQuat, this.flight.velocity, player, this);
    if (!ok) return;

    this.missilesRemaining--;
    this._emitLaunchSmoke(_turretWorld);
  }

  // Several white smoke particles scattered around the turret, drifting up and
  // out — the launch backblast/dust signature.
  _emitLaunchSmoke(origin) {
    if (!this.particleSystem) return;
    const n = this.params.firePuffCount;
    for (let i = 0; i < n; i++) {
      this.particleSystem._emit(
        'whiteSmoke',
        origin.x + srand() * 1.5,
        origin.y + rand(0, 1),
        origin.z + srand() * 1.5,
        srand() * 2,
        rand(0.5, 2),
        srand() * 2,
      );
    }
  }

  // ── Damage / destruction ─────────────────────────────────────────────────

  // Throttled damage smoke — buoyant ground-fire variant for the burning hull,
  // matching EnemyGroundVehicle. Stationary, so the trail has no drift velocity.
  _updateSmokeTrail(dt) {
    if (!this.particleSystem || !this.emitterNode) return;
    this._smokeTimer -= dt;
    if (this._smokeTimer > 0) return;
    this._smokeTimer = rand(SMOKE_INTERVAL_MIN, SMOKE_INTERVAL_MAX);

    this.emitterNode.getWorldPosition(_emitterWorld);
    if (this.destroyed || this.health < 40) {
      this.particleSystem.spawnGroundFireSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, 0, 0, 0);
    } else if (this.health < 60) {
      this.particleSystem.spawnBlackSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, 0, 0, 0);
    } else if (this.health < 80) {
      this.particleSystem.spawnWhiteSmokeTrail(_emitterWorld.x, _emitterWorld.y, _emitterWorld.z, 0, 0, 0);
    }
  }

  /**
   * Apply damage from a projectile hit. No-op on an already-destroyed wreck.
   * `source` is the weapon that landed it ('gau8' / 'aim9' / 'agm65'), which
   * only matters on the hit that kills — see _destroy.
   */
  onProjectileHit(_x, _y, _z, damage = 0, source = null) {
    if (this.destroyed) return;
    this.health = Math.max(0, this.health - damage);
    if (this.health <= 0) this._destroy(source);
  }

  // Kill: spawn explosion + audio, freeze turret/radar. Model stays in the
  // scene; `dead` remains false so EnemyManager doesn't prune the wreck.
  _destroy(source = null) {
    this.destroyed = true;
    this.flight.disabled = true;

    if (this.particleSystem) {
      this.particleSystem.spawnExplosion(
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
        { size: 1.4 });
      // Dirt lifted around the wreck — same as the driving vehicles, scaled to
      // match this hull's larger blast.
      this.particleSystem.spawnGroundDustBurst(
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
        { size: 1.4, terrainCollider: this._terrainCollider });
    }
    // A Maverick kill also gets a filmed fireball on top of the particle blast
    // (SimConfig.EXPLOSION_SPRITES). Gun kills deliberately don't: 30mm walks a
    // vehicle down over several passes, and a 30 m cinematic fireball on
    // whichever round happens to finish it reads as a different weapon.
    if (source === 'agm65') {
      this.explosionSystem?.spawnEvent('vehicleMaverickKill',
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
        { size: 1.15 });   // larger hull than the APCs, matching its particle blast
    }
    if (this.audioManager) {
      this.audioManager.playExplosion(
        'Explosion_Crash',
        this.flight.position.x, this.flight.position.y, this.flight.position.z,
      );
    }
  }

  dispose() {
    this.group.parent?.remove(this.group);
  }
}
